import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Loader2, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SEC-003: safely convert a chat message string into React nodes.
// Only two markers are honoured: **bold** and portal.sccfootball.com links.
// Everything else is rendered as plain text — no HTML injection surface.
const PORTAL_URL = 'portal.sccfootball.com';
const renderChatMessage = (content) => {
  if (!content) return null;
  // Split on **bold** first — odd indices are the bold captures.
  const parts = String(content).split(/\*\*(.*?)\*\*/g);
  const linkify = (text, key) => {
    // Split on the portal domain (case-insensitive) and turn each hit into an <a>
    const chunks = text.split(new RegExp(`(${PORTAL_URL})`, 'gi'));
    return chunks.map((chunk, i) =>
      chunk.toLowerCase() === PORTAL_URL ? (
        <a
          key={`${key}-l-${i}`}
          href="https://portal.sccfootball.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-primary/80"
        >
          {chunk}
        </a>
      ) : (
        <React.Fragment key={`${key}-t-${i}`}>{chunk}</React.Fragment>
      )
    );
  };
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={`b-${i}`}>{linkify(part, `b${i}`)}</strong>
    ) : (
      <React.Fragment key={`p-${i}`}>{linkify(part, `p${i}`)}</React.Fragment>
    )
  );
};

export const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm the SCC Football Academy Assistant. I can help you with questions about training schedules, pricing, registration, and more. How can I help you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I encountered an error. Please try again or contact the academy directly at portal.sccfootball.com" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetChat = async () => {
    try {
      await fetch(`${API_URL}/api/chat/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
    } catch (error) {
      console.error('Reset error:', error);
    }
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      content: "Hi! I'm the SCC Football Academy Assistant. I can help you with questions about training schedules, pricing, registration, and more. How can I help you today?"
    }]);
  };

  const suggestedQuestions = [
    "I have 2 kids (8 and 10), what are my options?",
    "How do SCC's rates compare to similar academies?",
    "How do I register?"
  ];

  return (
    <>
      {/* Floating Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <Button
              onClick={() => setIsOpen(true)}
              className="h-14 w-14 rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/30"
              data-testid="chat-open-btn"
            >
              <MessageCircle className="h-6 w-6" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)]"
          >
            <Card className="bg-background border-primary/30 shadow-2xl overflow-hidden flex flex-col h-[500px] max-h-[calc(100vh-100px)]">
              {/* Header */}
              <div className="bg-primary p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-foreground/20 rounded-full">
                    <Bot className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-primary-foreground">Academy Assistant</h3>
                    <p className="text-xs text-primary-foreground/80">Ask me about SCC Football</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={resetChat}
                    className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/20"
                    title="Reset conversation"
                    data-testid="chat-reset-btn"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsOpen(false)}
                    className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/20"
                    data-testid="chat-close-btn"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex-shrink-0 p-1.5 bg-primary/10 rounded-full h-fit">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg p-3 text-sm ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-card-foreground'
                      }`}
                    >
                      {/*
                        SEC-003: Render LLM output as safe React nodes.
                        Only recognised markers are allowed:
                          - **bold** → <strong>
                          - portal.sccfootball.com → clickable <a>
                        Everything else is a plain text node — impossible to inject HTML/JS.
                      */}
                      <p className="whitespace-pre-wrap">
                        {renderChatMessage(msg.content)}
                      </p>
                    </div>
                    {msg.role === 'user' && (
                      <div className="flex-shrink-0 p-1.5 bg-primary rounded-full h-fit">
                        <User className="h-4 w-4 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="flex-shrink-0 p-1.5 bg-primary/10 rounded-full h-fit">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Suggested Questions (show only if no user messages) */}
              {messages.length === 1 && (
                <div className="px-4 pb-2">
                  <p className="text-xs text-muted-foreground mb-2">Quick questions:</p>
                  <div className="flex flex-wrap gap-1">
                    {suggestedQuestions.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setInput(q);
                          inputRef.current?.focus();
                        }}
                        className="text-xs px-2 py-1 bg-muted hover:bg-primary/10 rounded-full text-muted-foreground hover:text-primary transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <form onSubmit={sendMessage} className="p-3 border-t border-border">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your question..."
                    className="flex-1 bg-muted rounded-lg px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    disabled={isLoading}
                    data-testid="chat-input"
                  />
                  <Button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="bg-primary hover:bg-primary/90"
                    size="icon"
                    data-testid="chat-send-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
